/**
 * Modal Render
 * Handles DOM creation and display control for element modal
 */

import { BoardId } from "../constants/board-id";
import { initializeDropdownMenus } from "../util/dropdown/dropdown-init";
import { setDropdownControlsInteractive } from "../util/dropdown/dropdown-handler";
import { phpPostData } from "../core/php-data-access";
import { ModalApiService } from "./modal-api";
import { SNSPreviewApi } from "../sns/preview-manager/sns-preview-api";
import { Roles } from "../constants/roles";
import { ScrollLock } from "../core/scroll-lock";
import commonUtil from "../util/common-utils";
import { setOgpFetchSuppressed } from "../util/ogp/ogp-fetch-suppression";
import { perfLogger } from "../core/perf-logger";
import { UISymbols } from "../constants/ui-symbols";
import { ModalSizes } from "../constants/modal-sizes";
import { LikeForm } from "../like/like-form";
import { linkifyHashtagsForBoardHtml } from "../post/content/post-content-hashtag-utils";
import { SNSLinksUtils } from "../sns/links/sns-links-utils";
import { toPositiveIntOrNull } from "../util/id-utils";
import { ModalForm } from "./modal-form";
import { ModalRenderListener } from "./modal-render-listener";

type ModalCommentData = {
    comment_id?: number;
    likes_count?: number;
    is_liked?: boolean;
};

type ModalCommentMapEntry = {
    likes_count?: number;
    is_liked?: boolean;
};

type ModalReviewAnswer = {
    answer_value?: string | number | null;
    question_type?: string | null;
    data_type?: string | null;
};

type ModalReviewData = {
    answers?: ModalReviewAnswer[] | null;
};

export type ModalSnsLink = string | { url?: string | null };

export type ModalReadPayload = {
    reads_count: number;
    post_id?: number;
    board_id?: string;
};

function preserveBoardMediaBadges(mediaArea: HTMLElement): HTMLElement[] {
    if (!(mediaArea instanceof HTMLElement)) return [];

    return Array.from(mediaArea.children).filter(
        (child): child is HTMLElement => child instanceof HTMLElement && child.classList.contains("board-media-badges"),
    );
}

function restoreBoardMediaBadges(mediaArea: HTMLElement, badges: HTMLElement[]): void {
    if (!(mediaArea instanceof HTMLElement) || !Array.isArray(badges) || badges.length === 0) return;

    badges
        .slice()
        .reverse()
        .forEach((badge) => {
            mediaArea.prepend(badge);
        });
}

export type ModalPostValue =
    | string
    | number
    | boolean
    | null
    | string[]
    | ModalSnsLink[]
    | ModalReviewData
    | ModalCommentData[]
    | Record<string, ModalCommentMapEntry>
    | { modal_size?: string | null; fixed_title?: string | null }
    | { product_id?: number; product_name?: string | null };

export type ModalPostData = {
    content?: string;
    post_title?: string | null;
    post_product_id?: number | null;
    user_id?: number | null;
    reads_count?: number;
    board_id?: string | null;
    post_id?: number | null;
    average_rating?: number | null;
    review_data?: ModalReviewData | null;
    board?: {
        modal_size?: string | null;
        fixed_title?: string | null;
    } | null;
    product?: {
        product_id?: number;
        product_name?: string | null;
    } | null;
    likes_count?: number;
    is_liked_by_current_user?: boolean;
    comments?: ModalCommentData[] | Record<string, ModalCommentMapEntry> | null;
    sns_links?: ModalSnsLink[] | null;
    content_images?: string[] | null;
    attached_images?: string[] | null;
    attached_videos?: string[] | null;
    [key: string]: ModalPostValue | undefined;
};

const modalForm = new ModalForm();

/**
 * Render hidden sns_links metadata block under parent post card.
 * @param {HTMLElement} postCard
 * @param {unknown} snsLinks
 */
export function renderParentSnsLinksData(postCard: HTMLElement, snsLinks: ModalSnsLink[] | null | undefined): void {
    if (!(postCard instanceof HTMLElement)) return;

    modalForm.getPostSnsLinksDataElements(postCard).forEach((element) => element.remove());

    if (!Array.isArray(snsLinks) || snsLinks.length === 0) {
        return;
    }

    const cleanedLinks = SNSLinksUtils.normalizeSnsLinks(snsLinks);

    if (cleanedLinks.length === 0) {
        return;
    }

    const safeLinks = commonUtil.escapeHtmlAttribute(JSON.stringify(cleanedLinks));
    postCard.insertAdjacentHTML(
        "beforeend",
        `<div class="sns-links-data" data-role="${Roles.POST_SNS_LINKS_DATA}" style="display:none;" data-sns-links="${safeLinks}"></div>`,
    );
}

/**
 * Render parent card content text + hidden sns content html.
 * @param {HTMLElement} contentElement
 * @param {string} newContent
 */
export function renderParentContentWithHiddenHtml(contentElement: HTMLElement, newContent: string): void {
    if (!(contentElement instanceof HTMLElement)) return;

    const parser = document.createElement("div");
    parser.innerHTML = String(newContent || "");
    const plainText = parser.textContent || parser.innerText || "";
    const truncated = plainText.substring(0, 150) + (plainText.length > 150 ? "..." : "");

    const existingSnsContent = modalForm.getSnsContent(contentElement);
    if (existingSnsContent) {
        existingSnsContent.remove();
    }

    contentElement.textContent = truncated;
    contentElement.insertAdjacentHTML(
        "beforeend",
        `<div class="sns-content" data-role="${Roles.SNS_CONTENT}" style="display:none;">${String(newContent || "")}</div>`,
    );
}

/**
 * Render board image grid items in parent card media area.
 * @param {HTMLElement} mediaArea
 * @param {{ images: string[], totalImages: number, boardId: string|null|undefined, userId: number|null|undefined }} options
 */
export function renderParentBoardImagesPreview(
    mediaArea: HTMLElement,
    {
        images,
        totalImages,
        boardId,
        userId,
    }: {
        images: string[];
        totalImages: number;
        boardId: string | null | undefined;
        userId: number | null | undefined;
    },
): void {
    if (!(mediaArea instanceof HTMLElement)) return;

    const preservedBadges = preserveBoardMediaBadges(mediaArea);

    modalForm.getBoardMediaPlaceholder(mediaArea)?.remove();

    let grid = modalForm.getBoardImagesGrid(mediaArea);
    if (!grid) {
        const existingVideo = modalForm.getBoardVideoWrap(mediaArea);
        if (existingVideo) {
            mediaArea.innerHTML = "";
            mediaArea.insertAdjacentHTML(
                "beforeend",
                `<div class="board-images-grid" data-role="${Roles.BOARD_IMAGES_GRID}"></div>`,
            );
            restoreBoardMediaBadges(mediaArea, preservedBadges);
            mediaArea.appendChild(existingVideo);
        } else {
            mediaArea.innerHTML = `<div class="board-images-grid" data-role="${Roles.BOARD_IMAGES_GRID}"></div>`;
            restoreBoardMediaBadges(mediaArea, preservedBadges);
        }
        grid = modalForm.getBoardImagesGrid(mediaArea);
    }

    if (!(grid instanceof HTMLElement)) return;

    const safeTotal = Number.isFinite(totalImages) ? totalImages : images.length;
    grid.setAttribute("data-count", String(images.length));
    const html = images
        .map((image, index) => {
            const url = commonUtil.resolvePostImageUrl(boardId, userId, image) ?? "";
            const safeSrc = commonUtil.escapeHtmlAttribute(String(url || ""));
            const overlay =
                index === 3 && safeTotal > 4
                    ? `<div class="image-more-overlay"><span class="more-text">+${safeTotal - 4}</span></div>`
                    : "";

            return `
                <div class="board-image-item">
                    ${overlay}
                    <img class="board-image" alt="Board Image ${index + 1}" src="${safeSrc}" loading="lazy">
                </div>
            `;
        })
        .join("");

    grid.innerHTML = html;
}

/**
 * Render board video preview in parent card media area.
 * @param {HTMLElement} mediaArea
 * @param {{ videoSrc: string, videoCount: number, hasImages: boolean }} options
 */
export function renderParentBoardVideoPreview(
    mediaArea: HTMLElement,
    { videoSrc, videoCount, hasImages }: { videoSrc: string; videoCount: number; hasImages: boolean },
): void {
    if (!(mediaArea instanceof HTMLElement)) return;

    const preservedBadges = preserveBoardMediaBadges(mediaArea);

    let videoWrap = modalForm.getBoardVideoWrap(mediaArea);
    if (!videoWrap) {
        if (hasImages) {
            mediaArea.insertAdjacentHTML(
                "beforeend",
                `<div class="board-video-wrap" data-role="${Roles.BOARD_VIDEO_WRAP}"></div>`,
            );
        } else {
            mediaArea.innerHTML = `<div class="board-video-wrap" data-role="${Roles.BOARD_VIDEO_WRAP}"></div>`;
            restoreBoardMediaBadges(mediaArea, preservedBadges);
        }
        videoWrap = modalForm.getBoardVideoWrap(mediaArea);
    }

    if (!(videoWrap instanceof HTMLElement)) return;

    const safeSrc = commonUtil.escapeHtmlAttribute(String(videoSrc || ""));
    const badgeHtml =
        Number(videoCount) > 1
            ? `<div class="video-count-badge"><i class="bi bi-camera-video"></i> ${Number(videoCount)}</div>`
            : "";

    videoWrap.innerHTML = `
        ${badgeHtml}
        <video class="board-video" controls preload="metadata" playsinline>
            <source src="${safeSrc}" type="video/mp4">
        </video>
    `;
}

/**
 * Replace media area with placeholder node.
 * @param {HTMLElement} mediaArea
 * @param {HTMLElement} placeholder
 */
export function renderParentBoardMediaPlaceholder(mediaArea: HTMLElement, placeholder: HTMLElement): void {
    if (!(mediaArea instanceof HTMLElement) || !(placeholder instanceof HTMLElement)) return;
    const preservedBadges = preserveBoardMediaBadges(mediaArea);
    mediaArea.innerHTML = "";
    restoreBoardMediaBadges(mediaArea, preservedBadges);
    mediaArea.appendChild(placeholder);
}

/**
 * Remove parent card video wrapper.
 * @param {HTMLElement} mediaArea
 */
export function removeParentBoardVideoPreview(mediaArea: HTMLElement): void {
    if (!(mediaArea instanceof HTMLElement)) return;
    modalForm.getBoardVideoWrap(mediaArea)?.remove();
}

class ModalRender {
    private overlay!: HTMLElement;
    private modal!: HTMLElement;
    private currentContent: HTMLElement | null;
    private currentPlaceholder: HTMLElement | null;
    private currentContentDetached: boolean;
    // Persisted posts use numeric postId; embedded create forms use string modalContentId.
    // Example: postId = 42, modalContentId = "review-create-5".
    private currentContentId: string | number | null;
    private youtubePreviewOriginalHtml: WeakMap<HTMLElement, string>;
    private detachedPostDetailHtmlCache: Map<string | number, string>;
    private readonly likeForm: LikeForm;
    private readonly modalForm: ModalForm;

    public constructor() {
        this.currentContent = null;
        this.currentPlaceholder = null;
        this.currentContentDetached = false;
        this.currentContentId = null;
        this.youtubePreviewOriginalHtml = new WeakMap();
        this.detachedPostDetailHtmlCache = new Map();
        this.likeForm = new LikeForm();
        this.modalForm = new ModalForm(this.likeForm);
    }

    public hasContentReady(contentId: string | number): boolean {
        if (typeof contentId === "number") {
            if (this.modalForm.getPostDetailById(contentId, document)) return true;
            if (this.detachedPostDetailHtmlCache.has(contentId)) return true;
        } else {
            if (this.modalForm.getPostDetailByModalContentId(contentId, document)) return true;
        }
        return false;
    }

    private readonly prefetchInFlight = new Set<number>();

    private static readonly MAX_PREFETCH_CONCURRENT = 3;

    public prefetchPostDetail(postId: number, boardId: string): void {
        if (!Number.isInteger(postId) || postId <= 0 || !boardId) return;
        if (this.hasContentReady(postId)) return;
        if (this.prefetchInFlight.has(postId)) return;
        if (this.prefetchInFlight.size >= ModalRender.MAX_PREFETCH_CONCURRENT) return;

        this.prefetchInFlight.add(postId);

        // XHR: prefetch modal HTML fragment into cache before the user clicks
        ModalApiService.fetchPostDetailDocument(postId, boardId)
            .then((doc) => {
                if (!(doc instanceof Document)) return;
                const fetched = this.extractFetchedPostDetail(doc, postId);
                if (fetched instanceof HTMLElement) {
                    this.detachedPostDetailHtmlCache.set(postId, fetched.outerHTML);
                }
            })
            .catch(() => {
                // prefetch failure is silent — modal open will retry
            })
            .finally(() => {
                this.prefetchInFlight.delete(postId);
            });
    }

    private isHiddenPostDetailSource(element: HTMLElement | null | undefined): boolean {
        return element instanceof HTMLElement && element.dataset.hiddenRole === Roles.POST_DETAIL_HIDDEN;
    }

    private getCachedPostDetail(contentId: string | number): HTMLElement | null {
        const cachedHtml = this.detachedPostDetailHtmlCache.get(contentId);
        if (typeof cachedHtml !== "string" || cachedHtml.trim() === "") {
            return null;
        }

        const clone = this.createElementFromHtml(cachedHtml);
        this.normalizeReusablePostDetailClone(clone);
        return clone;
    }

    /**
     * Create HTMLElement from HTML string.
     * @param {string} html
     * @returns {HTMLElement}
     */
    public createElementFromHtml(html: string): HTMLElement {
        const template = document.createElement("template");
        template.innerHTML = String(html || "").trim();
        const el = template.content.firstElementChild;
        if (!(el instanceof HTMLElement)) {
            throw new Error("Failed to create element from HTML");
        }
        return el;
    }

    /**
     * @returns {HTMLElement|null}
     */
    public getTitleElement() {
        if (!this.modal) return null;
        return this.modalForm.getElementModalTitle(this.modal);
    }

    /**
     * @param {HTMLElement} titleEl
     * @returns {string}
     */
    public getBaseTitle(titleEl: HTMLElement): string {
        const stored = titleEl.getAttribute("data-base-title");
        if (typeof stored === "string" && stored !== "") return stored;

        const raw = String(titleEl.textContent || "").trim();
        const base = raw.replace(/\s*\*+\s*$/, "").trim();
        titleEl.setAttribute("data-base-title", base);
        return base;
    }

    /**
     * @param {boolean} dirty
     */
    public setTitleDirty(dirty: boolean): void {
        const titleEl = this.getTitleElement();
        if (!titleEl) return;

        const base = this.getBaseTitle(titleEl);
        titleEl.setAttribute("data-title-dirty", dirty ? "1" : "0");
        titleEl.textContent = dirty ? `${base} *` : base;
    }

    /**
     * @returns {boolean}
     */
    public isTitleDirty() {
        const titleEl = this.getTitleElement();
        return titleEl?.getAttribute("data-title-dirty") === "1";
    }

    /**
     * Create modal structure
     */
    public createModalStructure() {
        this.removeExistingModal();
        this.createOverlay();
        this.createModal();
        this.appendToDocument();
    }

    /**
     * Remove existing modal if present
     */
    public removeExistingModal() {
        this.restoreContentToSource();
        const existingOverlay = this.modalForm.getElementModalOverlay(document);
        if (existingOverlay) {
            existingOverlay.remove();
        }
    }

    /**
     * Create overlay element
     */
    public createOverlay() {
        this.overlay = this.createElementFromHtml(`
            <div class="element-modal-overlay" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL_OVERLAY)}"></div>
        `);
    }

    /**
     * Create modal element
     */
    public createModal() {
        this.modal = this.createElementFromHtml(`
            <div class="modal element-modal" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL)}">
                <div class="modal-header element-modal-header" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL_HEADER)}">
                    <div class="section-title-sub modal-title" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL_TITLE)}"></div>
                    <button type="button" class="btn circle-clear modal-window element-modal-close" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL_CLOSE)}">${commonUtil.escapeHtml(UISymbols.MODAL_CLOSE)}</button>
                </div>
                <div class="modal-body element-modal-body" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL_BODY)}"></div>
                <div class="element-modal-footer" data-role="${commonUtil.escapeHtmlAttribute(Roles.ELEMENT_MODAL_FOOTER)}"></div>
            </div>
        `);
    }

    /**
     * Append modal to document
     */
    public appendToDocument() {
        this.overlay.appendChild(this.modal);
        document.body.appendChild(this.overlay);
    }

    /**
     * Load content into modal
     * @param {number} postId - Persisted post ID
     * @param {HTMLElement} link - Clicked link element
     * @param {Object|null} postData - Post data (optional, fetched if not provided)
     */
    public loadContent(postId: number, link: HTMLElement, postData: ModalPostData | null = null): void {
        void this.loadContentById(postId, link, postData).catch((error) => {
            console.error("[ModalRender] Failed to load modal content", error);
        });
    }

    /**
     * Create modal content is keyed by modalContentId, not by persisted post_id.
     * Example: "create-news" or "review-create-5".
     */
    public loadCreateContent(modalContentId: string, link: HTMLElement, postData: ModalPostData | null = null): void {
        void this.loadContentById(modalContentId, link, postData).catch((error) => {
            console.error("[ModalRender] Failed to load create modal content", error);
        });
    }

    /**
     * Inject XHR-fetched create modal fragment into the modal body.
     * The fragment is always treated as detached content (destroyed on close).
     */
    public injectXhrCreateContent(doc: Document, modalContentId: string, link: HTMLElement, postData: ModalPostData | null = null): void {
        this.restoreContentToSource();

        const postDetail =
            this.modalForm.getPostDetailByModalContentId(modalContentId, doc) ??
            (doc.body.firstElementChild instanceof HTMLElement ? doc.body.firstElementChild : null);

        if (!(postDetail instanceof HTMLElement)) {
            throw new Error("Create modal fragment missing post-detail element");
        }

        this.currentContent = postDetail;
        this.currentPlaceholder = null;
        this.currentContentDetached = true;
        this.currentContentId = modalContentId;

        postDetail.style.display = "block";

        this.setModalTitle(link, postData);
        this.applyModalSize(postData);
        this.insertContent(postDetail);
    }

    private async loadContentById(
        contentId: string | number,
        link: HTMLElement,
        postData: ModalPostData | null = null,
    ): Promise<void> {
        const perfSession = perfLogger.activeSession();
        perfSession?.mark("render.loadContent:begin", {
            postId: contentId,
        });

        this.restoreContentToSource();
        perfSession?.mark("render.restoreContentToSource:done");

        if (!postData && typeof contentId === "number") {
            postData = phpPostData(contentId) as ModalPostData | null;
        }

        const postDetailFromDocument =
            typeof contentId === "number"
                ? this.modalForm.getPostDetailById(contentId, document)
                : this.modalForm.getPostDetailByModalContentId(contentId, document);
        let postDetail = postDetailFromDocument ?? this.getCachedPostDetail(contentId);

        if (!postDetail && typeof contentId === "number") {
            postDetail = await this.fetchPostDetailForModal(contentId, link, postData);
        }

        if (!postDetail) {
            throw new Error(`Post detail not found for post ${contentId}`);
        }

        perfSession?.mark("render.postDetail:found");

        if (postDetailFromDocument) {
            if (!postDetail.parentNode) {
                throw new Error(`Post detail container missing parent for post ${contentId}`);
            }

            if (this.isHiddenPostDetailSource(postDetail)) {
                this.detachedPostDetailHtmlCache.set(contentId, postDetail.outerHTML);
            }

            const placeholder = this.createElementFromHtml(
                `<div data-modal-placeholder="${commonUtil.escapeHtmlAttribute(String(contentId))}"></div>`,
            );
            postDetail.parentNode.insertBefore(placeholder, postDetail);
            this.currentPlaceholder = placeholder;
            this.currentContentDetached = this.isHiddenPostDetailSource(postDetail);
        } else {
            this.currentPlaceholder = null;
            this.currentContentDetached = true;
        }

        this.currentContent = postDetail;
        this.currentContentId = contentId;

        postDetail.style.display = "block";

        perfSession?.mark("render.postData:ready", {
            hasPostData: !!postData,
            contentLen: typeof postData?.content === "string" ? postData.content.length : 0,
        });

        this.setModalTitle(link, postData);
        this.applyModalSize(postData);

        perfSession?.mark("render.title+size:done");

        this.insertContent(postDetail);
        perfSession?.mark("render.insertContent:done");
        this.ensurePostContentHtml(postData);
        perfSession?.mark("render.ensurePostContentHtml:done");
        this.ensureYouTubePreviewDelegation();
        this.syncLikeCounts(postData);

        perfSession?.mark("render.loadContent:end");
    }

    private resolvePostDetailUrl(postId: number, link: HTMLElement, postData: ModalPostData | null): string | null {
        const boardId =
            (typeof postData?.board_id === "string" ? postData.board_id : "") ||
            link.dataset.boardId ||
            this.modalForm.getClosestPostCard(link)?.dataset.boardId ||
            this.modalForm.getClosestPostDetail(link)?.dataset.boardId ||
            "";

        const normalizedBoardId = String(boardId || "").trim();
        if (!normalizedBoardId) return null;

        return commonUtil.resolveUrl(`/posts/${normalizedBoardId}/posts/${postId}`);
    }

    private extractFetchedPostDetail(doc: Document, postId: number): HTMLElement | null {
        const direct = this.modalForm.getPostDetailById(postId, doc);
        if (direct instanceof HTMLElement) {
            // Traverse up to .post-detail-layout so the modal gets the full layout
            // including the footer and the parent context required by CSS rules
            // (.element-modal-body .post-detail-layout .post-detail-content).
            const layout = this.modalForm.getClosestPostDetailLayout(direct) ?? direct;
            layout.classList.remove("post-detail-layout--modeless");
            this.normalizeReusablePostDetailClone(layout);
            return layout;
        }

        const fallback = this.modalForm.getWrappedPostDetail(doc);
        if (fallback instanceof HTMLElement) {
            this.normalizeReusablePostDetailClone(fallback);
            return fallback;
        }

        return null;
    }

    private async fetchPostDetailForModal(
        postId: number,
        link: HTMLElement,
        postData: ModalPostData | null,
    ): Promise<HTMLElement | null> {
        const perfSession = perfLogger.activeSession();
        const detailUrl = this.resolvePostDetailUrl(postId, link, postData);
        if (!detailUrl) {
            perfSession?.mark("render.fetchPostDetail:skip-no-url", { postId });
            return null;
        }

        try {
            perfSession?.mark("render.fetchPostDetail:begin", { postId, url: detailUrl });
            const boardId = String(detailUrl.match(/\/posts\/([^/]+)\/posts\//)?.[1] || "").trim();
            if (!boardId) {
                perfSession?.mark("render.fetchPostDetail:skip-no-board", { postId });
                return null;
            }

            const doc = await ModalApiService.fetchPostDetailDocument(postId, boardId);
            if (!(doc instanceof Document)) {
                perfSession?.mark("render.fetchPostDetail:empty", { postId });
                return null;
            }

            const fetchedDetail = this.extractFetchedPostDetail(doc, postId);
            if (!(fetchedDetail instanceof HTMLElement)) {
                perfSession?.mark("render.fetchPostDetail:not-found", { postId });
                return null;
            }

            const totalLen = doc.documentElement.outerHTML.length;
            const usedLen = fetchedDetail.outerHTML.length;
            const cutRatio = totalLen > 0 ? Math.round((usedLen / totalLen) * 100) : 0;
            console.log(
                `[ModalRender] fetchPostDetail postId=${postId}` +
                    ` total=${totalLen}B used=${usedLen}B cutRatio=${cutRatio}%` +
                    ` discarded=${totalLen - usedLen}B`,
            );

            this.detachedPostDetailHtmlCache.set(postId, fetchedDetail.outerHTML);
            perfSession?.mark("render.fetchPostDetail:done", { postId, totalLen, usedLen, cutRatio });
            return this.getCachedPostDetail(postId);
        } catch (error) {
            console.warn("[ModalRender] Failed to fetch post detail for modal", error);
            perfSession?.mark("render.fetchPostDetail:error", {
                postId,
                message: error instanceof Error ? error.message : String(error),
            });
            return null;
        }
    }

    /**
     * Replace create modal source DOM with a fresh clone from Blade template snapshot.
     * modalContentId is a UI-only key for embedded create forms.
     * @returns {boolean}
     */
    public replaceCreateSourceWithInitialClone(modalContentId: string): boolean {
        const source = this.modalForm.getPostDetailByModalContentId(modalContentId, document);
        const templateClone = this.cloneCreateSourceFromTemplate(modalContentId);

        if (!(source instanceof HTMLElement) || !(templateClone instanceof HTMLElement)) return false;

        source.replaceWith(templateClone);

        if (this.currentContent === source) {
            this.currentContent = null;
            this.currentPlaceholder = null;
            this.currentContentId = null;
        }

        return true;
    }

    /**
     * Clone create-source wrapper from Blade-managed template snapshot.
     * @param {string} modalContentId
     * @returns {HTMLElement|null}
     */
    public cloneCreateSourceFromTemplate(modalContentId: string): HTMLElement | null {
        const tpl = this.modalForm.getCreateTemplateByModalContentId(modalContentId, document);
        if (!(tpl instanceof HTMLTemplateElement)) return null;

        const candidate = tpl.content.firstElementChild;
        if (!(candidate instanceof HTMLElement)) return null;

        const clone = candidate.cloneNode(true);
        if (!(clone instanceof HTMLElement)) return null;
        this.normalizeCreateFormClone(clone);
        return clone;
    }

    /**
     * Remove stale WYSIWYG/Quill DOM before reusing create-form clones.
     * Prevents duplicate editor mounts (e.g. duplicate Content sections).
     *
     * @param {HTMLElement} root
     */
    public normalizeCreateFormClone(root: HTMLElement): void {
        if (!(root instanceof HTMLElement)) return;

        const createForm = this.modalForm.getPostCreateForm(root);
        if (createForm instanceof HTMLFormElement) {
            createForm.dataset.dirty = "0";
        }

        // Chunk-level cleanup: clear transient init/bound flags in one traversal.
        const transientAttrs = [
            "data-dirty-tracker-attached",
            "data-validation-attached",
            "data-back-number-init",
            "data-sns-links-panel-init",
            "data-animated-toggle-bound",
            "data-char-counter-bound",
        ];
        const nodes = [root, ...this.modalForm.getAllElements(root)];
        nodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            transientAttrs.forEach((attr) => node.removeAttribute(attr));
        });

        this.modalForm.getWysiwygTextareas(root).forEach((textarea) => {
            if (!(textarea instanceof HTMLTextAreaElement)) return;

            const parent = textarea.parentElement;
            if (!(parent instanceof HTMLElement)) return;

            this.modalForm.getWysiwygToolbars(parent).forEach((node) => node.remove());
            this.modalForm.getWysiwygEditors(parent).forEach((node) => node.remove());

            textarea.classList.remove("wysiwyg-fallback");
        });
    }

    public normalizeReusablePostDetailClone(root: HTMLElement): void {
        if (!(root instanceof HTMLElement)) return;

        const transientAttrs = [
            "data-dirty-tracker-attached",
            "data-validation-attached",
            "data-back-number-init",
            "data-sns-links-panel-init",
            "data-animated-toggle-bound",
            "data-char-counter-bound",
            "data-search-menu-initialized",
            "data-editing",
        ];

        const nodes = [root, ...this.modalForm.getAllElements(root)];
        nodes.forEach((node) => {
            if (!(node instanceof HTMLElement)) return;
            transientAttrs.forEach((attr) => node.removeAttribute(attr));
        });

        this.modalForm.getWysiwygTextareas(root).forEach((textarea) => {
            if (!(textarea instanceof HTMLTextAreaElement)) return;

            delete textarea.dataset.wysiwygInitialized;
            textarea.classList.remove("wysiwyg-fallback");

            const parent = textarea.parentElement;
            if (!(parent instanceof HTMLElement)) return;

            this.modalForm.getWysiwygToolbars(parent).forEach((node) => node.remove());
            this.modalForm.getWysiwygEditors(parent).forEach((node) => node.remove());
        });
    }

    /**
     * Ensure the element modal displays the latest HTML content.
     *
     * Background:
     * - Index pages keep a hidden "post detail" DOM stash for element modal.
     * - In some cases the stash may not include embedded content media (e.g. <img>),
     *   even though postsData/content contains it.
     * - When that happens, the modal shows only text.
     *
     * This method repairs the modal's visible content using postData.content.
     *
     * @param {Object|null} postData
     */
    public ensurePostContentHtml(postData: ModalPostData | null): void {
        const perfSession = perfLogger.activeSession();
        const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

        try {
            const html = typeof postData?.content === "string" ? postData.content : "";
            const boardId = typeof postData?.board_id === "string" ? postData.board_id : "";
            if (!html) return;

            const modalBody = this.getModalBody();
            if (!modalBody) return;

            const contentElement = this.modalForm.getPostDetailContent(modalBody);
            if (!contentElement) return;

            const currentHtml = contentElement.innerHTML;
            const shouldNormalizeCurrentHtml =
                boardId.trim() !== "" &&
                !currentHtml.includes("post-hashtag-link") &&
                (currentHtml.includes("#") || currentHtml.includes("?q=%23"));

            if (shouldNormalizeCurrentHtml) {
                contentElement.innerHTML = linkifyHashtagsForBoardHtml(currentHtml, boardId);
            }

            const targetHasImg = /<img\b/i.test(html);
            const currentHasImg = this.modalForm.hasImage(contentElement);

            // Only overwrite when we expect images but none are present in DOM.
            if (targetHasImg && !currentHasImg) {
                perfSession?.mark("render.ensurePostContentHtml:overwrite", {
                    htmlLen: html.length,
                });
                contentElement.innerHTML = linkifyHashtagsForBoardHtml(html, boardId);

                // Preserve hidden sns_links metadata for preview generation if possible.
                const snsLinks = SNSLinksUtils.normalizeSnsLinks(postData?.sns_links || []);
                if (snsLinks.length > 0) {
                    const snsLinksData = this.createElementFromHtml(`
                        <div
                            class="sns-links-data"
                            data-role="${commonUtil.escapeHtmlAttribute(Roles.POST_SNS_LINKS_DATA)}"
                            style="display:none"
                            data-sns-links="${commonUtil.escapeHtmlAttribute(JSON.stringify(snsLinks))}">
                        </div>
                    `);
                    contentElement.appendChild(snsLinksData);
                }
            }
        } catch (e) {
            console.warn("[ModalRender] Failed to ensure post content HTML", e);
            perfSession?.mark("render.ensurePostContentHtml:error", {
                message: e instanceof Error ? e.message : String(e),
            });
        } finally {
            const t1 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
            perfSession?.mark("render.ensurePostContentHtml:timing", {
                ms: Math.round((t1 - t0) * 1000) / 1000,
            });
        }
    }

    /**
     * Set modal title
     * @param {HTMLElement} link - Clicked link element
     * @param {Object|null} postData - Post data
     */
    public setModalTitle(link: HTMLElement, postData: ModalPostData | null = null): void {
        const titleElement = this.getTitleElement();
        if (!titleElement) {
            console.warn("[ModalRender] Title element not found");
            return;
        }

        const postId = toPositiveIntOrNull(
            link.dataset.postId ?? this.modalForm.getClosestPostIdElement(link)?.dataset.postId,
        );

        if (postId !== null && postData) {
            const resolved = this.resolvePostTitle(postData);
            titleElement.textContent = resolved;
            titleElement.setAttribute("data-base-title", resolved);
            this.setTitleDirty(this.isTitleDirty());
            return;
        }

        // No postData: fallback to nearest rendered post title
        const closestPostContainer = this.modalForm.getClosestPostContainer(link);
        const closestTitleElement =
            closestPostContainer instanceof HTMLElement
                ? this.modalForm.getFirstTitleDataElement(closestPostContainer)
                : null;
        const nearestPostTitle =
            closestTitleElement instanceof HTMLElement
                ? String(closestTitleElement.dataset.postTitle || "").trim()
                : "";
        const fallbackTitle = nearestPostTitle || link.dataset.postTitle || link.textContent.trim() || "";
        titleElement.textContent = fallbackTitle;
        titleElement.setAttribute("data-base-title", fallbackTitle);
        this.setTitleDirty(this.isTitleDirty());
    }

    /**
     * Resolve the display title for the modal header.
     *
     * Priority:
     * 1. post_title (non-empty, not the token) → use as-is
     * 2. post_title empty or equals "{{product_name}}" token:
     *    a. board.fixed_title === "{{product_name}}" → use linked product_name
     *    b. board.fixed_title non-empty → use fixed_title literal
     *    c. both absent → blank
     */
    private resolvePostTitle(postData: ModalPostData): string {
        const PRODUCT_NAME_TOKEN = "{{product_name}}";

        const postTitle = String(postData.post_title ?? "").trim();
        if (postTitle !== "" && postTitle !== PRODUCT_NAME_TOKEN) {
            return postTitle;
        }

        const fixedTitle = String(postData.board?.fixed_title ?? "").trim();
        if (fixedTitle === PRODUCT_NAME_TOKEN) {
            return String(postData.product?.product_name ?? "").trim();
        }
        if (fixedTitle !== "") {
            return fixedTitle;
        }

        return "";
    }

    public applyModalSize(postData: ModalPostData | null = null): void {
        // Remove existing size classes
        this.modal.classList.remove(...ModalSizes.ALL_CLASSES);

        // Review cards should always use the largest modal.
        const isReviewBoard = String(postData?.board_id || "").toLowerCase() === BoardId.REVIEW;
        if (isReviewBoard) {
            this.modal.classList.add(ModalSizes.CLASS_EXTRA_LARGE);
            return;
        }

        const modalSize = ModalSizes.fromBoardModalSize(postData?.board?.modal_size);
        if (modalSize) {
            this.modal.classList.add(ModalSizes.toClassName(modalSize));
            return;
        }

        this.modal.classList.add(ModalSizes.CLASS_EXTRA_LARGE);
    }

    /**
     * Insert content into modal body
     * @param {HTMLElement} content - Content element
     */
    public insertContent(content: HTMLElement): void {
        const perfSession = perfLogger.activeSession();
        const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

        const modalBody = this.getModalBody();
        const modalFooter = this.getModalFooter();
        if (!modalBody || !modalFooter) return;
        modalBody.innerHTML = "";
        modalFooter.innerHTML = "";

        const extractedFooter = this.modalForm.getPostDetailModalFooter(content);
        if (extractedFooter instanceof HTMLElement) {
            modalFooter.appendChild(extractedFooter);
        }

        modalBody.appendChild(content);

        const tDom = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        perfSession?.mark("render.insertContent:dom", {
            ms: Math.round((tDom - t0) * 1000) / 1000,
        });

        // Always start each open with a clean title state.
        this.setTitleDirty(false);

        // When re-opening embedded create forms, clear previous validation styling and dirty state.
        // (The init registry may not always re-run setup for cached DOM stashes.)
        this.resetEmbeddedCreateFormState(modalBody);

        // Initialize dropdown menus after content is inserted
        initializeDropdownMenus();
        setDropdownControlsInteractive(modalBody, true);

        const tDropdown = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        perfSession?.mark("render.insertContent:dropdown", {
            ms: Math.round((tDropdown - tDom) * 1000) / 1000,
        });

        // Defer heavy init (event dispatch, media setup, SNS init) to the next frame
        // so the inserted content is painted before any blocking work runs.
        const deferredInit = () => {
            const td0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

            try {
                document.dispatchEvent(
                    new CustomEvent("app:content:inserted", {
                        detail: { root: modalBody },
                    }),
                );
            } catch (_e) {
                // CustomEvent may not be available in very old browsers; ignore
            }

            const tInserted = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
            perfSession?.mark("render.insertContent:event", {
                ms: Math.round((tInserted - td0) * 1000) / 1000,
            });

            this.initializeSNSPreviews();

            const tEnd = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
            perfSession?.mark("render.insertContent:snsInit", {
                ms: Math.round((tEnd - tInserted) * 1000) / 1000,
            });
            perfSession?.mark("render.insertContent:total", {
                ms: Math.round((tEnd - t0) * 1000) / 1000,
            });
        };

        try {
            requestAnimationFrame(deferredInit);
        } catch (_e) {
            setTimeout(deferredInit, 0);
        }
    }

    /**
     * Reset the embedded post-create form state.
     * This prevents validation styles from persisting across modal open/close cycles.
     * @param {Element|HTMLElement|null} root
     */
    public resetEmbeddedCreateFormState(root: Element | HTMLElement | null): void {
        if (!(root instanceof Element)) return;

        const createForm = this.modalForm.getPostCreateForm(root);
        if (!createForm) return;

        try {
            createForm.dataset.dirty = "0";
        } catch (_e) {
            // ignore
        }

        try {
            this.modalForm.getInvalidElements(createForm).forEach((element) => element.classList.remove("is-invalid"));
        } catch (_e) {
            // ignore
        }
    }

    /**
     * Initialize SNS previews in modal content
     */
    public initializeSNSPreviews() {
        try {
            const modalBody = this.getModalBody();
            if (!modalBody) {
                console.warn("[ModalRender] Modal body not found");
                return;
            }

            const postContent = this.modalForm.getPostDetailContent(modalBody);

            if (postContent) {
                // Reset data-sns-processed to allow re-processing in modal
                postContent.removeAttribute("data-sns-processed");

                // Initialize SNS preview for display (modal view)
                SNSPreviewApi.initForDisplay(
                    `[data-role="${Roles.ELEMENT_MODAL_BODY}"] [data-role="${Roles.POST_DETAIL_CONTENT}"]`,
                    false,
                );

                // Add SNS icon badges to media area — no tile creation in modal (SSR renders images at full scale).
                SNSPreviewApi.initForIndex(
                    `[data-role="${Roles.ELEMENT_MODAL_BODY}"] [data-role="${Roles.POST_DETAIL}"]`,
                    false,
                );
            } else {
                // Create modal doesn't include post display content by design.
                const hasEmbeddedCreateForm = this.modalForm.getPostCreateForm(modalBody) !== null;
                if (!hasEmbeddedCreateForm) {
                    console.warn("[ModalRender] No post display content found in modal body");
                }
            }
        } catch (_e) {
            console.error("Failed to initialize SNS previews in modal", _e);
        }
    }

    /**
     * Sync like counts from post data to modal content
     * @param {string} postId - Post ID
     * @param {Object|null} postData - Post data
     */
    public syncLikeCounts(postData: ModalPostData | null = null): void {
        if (!postData) {
            return;
        }
        const modalRoot = this.getModal();

        if (!modalRoot) {
            return;
        }

        // Update post like count
        if (postData.likes_count !== undefined) {
            const postLikeBtn = this.modalForm.getPostLikeButton(modalRoot);
            if (postLikeBtn) {
                const likeCountSpan = this.likeForm.getCountElement(postLikeBtn);
                if (likeCountSpan) {
                    likeCountSpan.textContent = String(postData.likes_count);
                }

                // Update liked state
                if (postData.is_liked_by_current_user !== undefined) {
                    const isLiked = postData.is_liked_by_current_user;
                    postLikeBtn.dataset.isLiked = isLiked ? "true" : "false";
                    postLikeBtn.classList.toggle("liked", isLiked);

                    const icon = this.likeForm.getIconElement(postLikeBtn);
                    if (icon) {
                        icon.className = isLiked ? "bi bi-heart-fill" : "bi bi-heart";
                    }
                }
            }
        }

        // Update comment like counts (handle both array and object format)
        if (postData.comments) {
            // Handle array format (from API response)
            if (Array.isArray(postData.comments)) {
                postData.comments.forEach((comment) => {
                    const commentId = comment.comment_id;
                    if (!Number.isInteger(commentId) || commentId <= 0) return;
                    const commentLikeBtn = this.modalForm.getCommentLikeButton(modalRoot, commentId);

                    if (commentLikeBtn && comment.likes_count !== undefined) {
                        if (commentLikeBtn) {
                            const likeCountSpan = this.likeForm.getCountElement(commentLikeBtn);
                            if (likeCountSpan) {
                                likeCountSpan.textContent = String(comment.likes_count);
                            }

                            // Update liked state
                            if (comment.is_liked !== undefined) {
                                const isLiked = comment.is_liked;
                                commentLikeBtn.dataset.isLiked = isLiked ? "true" : "false";
                                commentLikeBtn.classList.toggle("liked", isLiked);

                                const icon = this.likeForm.getIconElement(commentLikeBtn);
                                if (icon) {
                                    icon.className = isLiked ? "bi bi-heart-fill" : "bi bi-heart";
                                }
                            }
                        }
                    }
                });
            }
            // Handle object format (from syncCommentLikeToParent)
            else if (typeof postData.comments === "object") {
                Object.entries(
                    postData.comments as Record<string, { likes_count?: number; is_liked?: boolean }>,
                ).forEach(([commentId, commentData]) => {
                    const normalizedCommentId = Number(commentId);
                    if (!Number.isInteger(normalizedCommentId) || normalizedCommentId <= 0) return;
                    const commentLikeBtn = this.modalForm.getCommentLikeButton(modalRoot, normalizedCommentId);

                    if (commentLikeBtn) {
                        if (commentLikeBtn) {
                            // Update like count
                            if (commentData.likes_count !== undefined) {
                                const likeCountSpan = this.likeForm.getCountElement(commentLikeBtn);
                                if (likeCountSpan) {
                                    likeCountSpan.textContent = String(commentData.likes_count);
                                }
                            }

                            // Update liked state
                            if (commentData.is_liked !== undefined) {
                                const isLiked = commentData.is_liked;
                                commentLikeBtn.dataset.isLiked = isLiked ? "true" : "false";
                                commentLikeBtn.classList.toggle("liked", isLiked);

                                const icon = this.likeForm.getIconElement(commentLikeBtn);
                                if (icon) {
                                    icon.className = isLiked ? "bi bi-heart-fill" : "bi bi-heart";
                                }
                            }
                        }
                    }
                });
            }
        }
    }

    /**
     * Show loading spinner in modal body while content is being fetched.
     * Replaced automatically when insertContent() is called.
     */
    public showLoadingSpinner(): void {
        const modalBody = this.getModalBody();
        if (!modalBody) return;
        modalBody.innerHTML = `<div class="element-modal-body-loading"><div class="spinner"></div></div>`;
    }

    /**
     * Show modal with animation
     */
    public showModal() {
        const perfSession = perfLogger.activeSession();
        const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();

        // Performance: suppress OGP API calls while the element modal is open.
        // Index pages often keep many hidden post-detail nodes; rendering OGP in the background
        // can make modal interactions feel sluggish.
        setOgpFetchSuppressed(true);

        // Always reset draggable inline positioning so open starts from CSS default.
        this.modal.style.removeProperty("left");
        this.modal.style.removeProperty("top");
        this.modal.style.removeProperty("right");
        this.modal.style.removeProperty("bottom");
        this.modal.style.removeProperty("margin");
        this.modal.style.removeProperty("position");
        this.modal.style.removeProperty("transform");
        this.modal.style.removeProperty("will-change");
        this.modal.classList.remove("is-dragging");
        delete this.modal.dataset.dragInitialized;

        this.overlay.style.display = "flex";
        const tReflow0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        this.overlay.offsetHeight; // Force reflow
        const tReflow1 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        perfSession?.mark("render.showModal:forcedReflow", {
            ms: Math.round((tReflow1 - tReflow0) * 1000) / 1000,
        });
        this.overlay.style.opacity = "1";

        // Reset scroll position to top
        const modalBody = this.getModalBody();
        if (modalBody) {
            modalBody.scrollTop = 0;
        }

        setTimeout(() => {
            this.modal.style.transform = "scale(1)";
            this.modal.style.opacity = "1";

            // Let modal-aware components recalculate after the element becomes visible.
            globalThis.dispatchEvent(new Event("resize"));
        }, 10);

        ScrollLock.lock();

        const t1 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        perfSession?.mark("render.showModal:done", {
            ms: Math.round((t1 - t0) * 1000) / 1000,
        });
    }

    /**
     * Close modal with animation
     */
    public closeModal() {
        this.modal.style.transform = "scale(0.9)";
        this.modal.style.opacity = "0";
        this.overlay.style.opacity = "0";

        // Allow OGP API calls again once the modal is closing.
        setOgpFetchSuppressed(false);

        setTimeout(() => {
            this.overlay.style.display = "none";
            ScrollLock.unlock();

            this.restoreContentToSource();

            const modalBody = this.getModalBody();
            if (modalBody) {
                modalBody.innerHTML = "";
            }

            this.modal.style.transform = "scale(0.9)";
            this.modal.style.opacity = "0";
        }, 200);
    }

    /**
     * Check if modal is open
     * @returns {boolean}
     */
    public isOpen(): boolean {
        if (!(this.overlay instanceof HTMLElement) || !(this.modal instanceof HTMLElement)) {
            return false;
        }

        return (
            window.getComputedStyle(this.overlay).display !== "none" &&
            window.getComputedStyle(this.modal).display !== "none"
        );
    }

    /**
     * Get modal body element
     * @returns {HTMLElement|null}
     */
    public getModalBody(): HTMLElement | null {
        if (!this.modal) return null;
        return this.modalForm.getElementModalBody(this.modal);
    }

    public getModalFooter(): HTMLElement | null {
        if (!this.modal) return null;
        return this.modalForm.getElementModalFooter(this.modal);
    }

    /**
     * Get overlay element
     * @returns {HTMLElement|null}
     */
    public getOverlay(): HTMLElement | null {
        return this.overlay || null;
    }

    /**
     * Get modal element
     * @returns {HTMLElement|null}
     */
    public getModal(): HTMLElement | null {
        return this.modal || null;
    }

    /**
     * Ensure delegated click handler for YouTube previews inside modal
     */
    public ensureYouTubePreviewDelegation() {
        if (!this.modal) {
            return;
        }

        const modalBody = this.getModalBody();
        if (!modalBody || modalBody.dataset.youtubeDelegation === "true") {
            return;
        }

        ModalRenderListener.bindYouTubePreviewClick(modalBody, (event) => {
            const playButton = this.modalForm.getClosestYouTubePlayButton(event.target);
            if (!playButton) {
                return;
            }

            const previewContainer = this.modalForm.getClosestYouTubePreview(playButton);
            if (!previewContainer) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            const videoId = previewContainer.dataset.youtubeVideoId || playButton.dataset.youtubeVideoId || "";

            if (!videoId || this.modalForm.getIframe(previewContainer)) {
                return;
            }

            if (!this.youtubePreviewOriginalHtml.has(previewContainer)) {
                this.youtubePreviewOriginalHtml.set(previewContainer, previewContainer.innerHTML);
            }

            previewContainer.innerHTML = "";

            const iframe = document.createElement("iframe");
            iframe.src = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
            iframe.style.cssText = `
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    border: none;
                `;
            iframe.setAttribute("allowfullscreen", "true");
            iframe.setAttribute("allow", "autoplay; encrypted-media");

            previewContainer.appendChild(iframe);
        });

        modalBody.dataset.youtubeDelegation = "true";
    }

    /**
     * Move the current modal content back to its original hidden wrapper
     */
    public restoreContentToSource() {
        if (!this.currentContent) {
            return;
        }

        const modalFooter = this.getModalFooter();
        const detachedFooter = modalFooter?.firstElementChild;
        const layout = this.modalForm.getPostDetailLayout(this.currentContent);
        if (detachedFooter instanceof HTMLElement && layout instanceof HTMLElement) {
            layout.appendChild(detachedFooter);
        }

        const placeholderParent = this.currentPlaceholder?.parentNode ?? null;

        // Clear create form validation styles before moving content back.
        // This ensures subsequent opens start clean even if setup doesn't re-run.
        this.resetEmbeddedCreateFormState(this.currentContent);

        this.resetYouTubePreviews(this.currentContent);

        if (this.currentContentDetached) {
            this.currentContent.remove();
            this.currentPlaceholder?.remove();
        } else {
            this.currentContent.style.display = "none";

            if (this.currentPlaceholder && placeholderParent) {
                placeholderParent.replaceChild(this.currentContent, this.currentPlaceholder);
            } else {
                this.currentPlaceholder?.remove();
            }
        }

        this.currentContent = null;
        this.currentPlaceholder = null;
        this.currentContentDetached = false;
        this.currentContentId = null;

        if (modalFooter) {
            modalFooter.innerHTML = "";
        }

        // Clear dirty marker when closing.
        this.setTitleDirty(false);
    }

    /**
     * Restore YouTube previews to their thumbnail state and stop playback
     * @param {HTMLElement} container
     */
    public resetYouTubePreviews(container: HTMLElement): void {
        if (!container) {
            return;
        }

        const previews = this.modalForm.getYouTubePreviews(container);
        previews.forEach((preview) => {
            if (!(preview instanceof HTMLElement)) {
                return;
            }

            const iframe = this.modalForm.getIframe(preview);

            if (!iframe) {
                return;
            }

            try {
                iframe.src = "about:blank";
            } catch (error) {
                console.warn("Failed to reset YouTube iframe", error);
            }

            const originalHtml = this.youtubePreviewOriginalHtml.get(preview);
            if (typeof originalHtml === "string") {
                preview.innerHTML = originalHtml;
            } else {
                preview.innerHTML = "";
            }
        });
    }
}

export { ModalRender };
